/* eslint-disable react/jsx-closing-tag-location */
import React, { useState, lazy, Suspense, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import { Navigation } from './Navigation';
import BurgerMenuIcon from '../../styles/clients/motorplay/assets/burger_menu.png';
import { getProductByProductId } from '../../utils/products';
import { PaymentGateways } from '../../enums';

const InvoicesHistory = lazy(() => import('./invoices/InvoiceHistory'));
const PaymentMethods = lazy(() => import('../../containers/MyAccount/PaymentMethods'));
const GeneralInformation = lazy(() => import('./generalInformation'));
const Subscription = lazy(() => import('./subscription/index'));
const NewPaymentMethod = lazy(() => import('../../containers/NewPaymentMethod'));

const MyAccount = (props) => {
  const {
    path,
    planSubscription,
    addOnsSubscription,
    client,
    isSubscriptionFromApps,
    invoices,
    countryCode,
    user,
    products
  } = props

  const params = useParams();
  const tab = params.tab || 'myaccount';
  const [mobileCollapse, setMobileCollapse] = useState(false);
  const hasPreMigrationSubscription = useMemo(
    () => {
      const productData = getProductByProductId(planSubscription?.productId, products);
      return !!productData?.customData?.some(
        obj => obj.key === "oldSubscription" && obj.value === "true"
      );
    },
    [planSubscription, products]
  );
  const hasPlanSubscriptionWithFreepass = useMemo(
    () => planSubscription?.purchaseState?.paymentMethod === PaymentGateways.freepass,
    [planSubscription, products]
  );
  const hasPlanSubscriptionWithAffiliates = useMemo(
    () => planSubscription?.purchaseState?.paymentMethod === PaymentGateways.affiliates,
    [planSubscription, products]
  );
  
  const redirect = () => {
    const cloudpayConfig = window.AppENV.cloudpay;
    window.location.href = `${cloudpayConfig.baseURL}/redirect/goBackToReturnUrl${window.location.search}&jwt=${localStorage.getItem('jwt')}`;
  };

  const handleBackToMyAccount = () => {
    redirect(path);
  };

  const checkActivePlanAndPaymentMethod = () => {
    if (planSubscription) {
      return planSubscription?.purchaseState?.paymentMethod !== 'affiliates'
    }
    return false;
  }

  // If has a plan subscription with another payment method different than affiliates means a subscription was bought
  const hasInvoices = useMemo(() => checkActivePlanAndPaymentMethod(), [planSubscription]);

  return (
    <div className="my-account container">
      <Row className="my-account__header header">
        <Col>
          <h2 className="header__title">
            <FormattedMessage
              id="MyAccount.title"
            />
          </h2>
        </Col>
      </Row>
      <Row className="my-account__content">
        <Col md="3" lg="3" className="my-account__navigation desktopMenu">
          <Navigation
            tab={tab}
            history={props.history}
            client={client}
            handleBackToMyAccount={handleBackToMyAccount}
            isSubscriptionFromApps={isSubscriptionFromApps}
            hasInvoices={hasInvoices && !hasPreMigrationSubscription}
            hasPreMigrationSubscription={hasPreMigrationSubscription}
            hasPlanSubscriptionWithFreepass={hasPlanSubscriptionWithFreepass}
            hasPlanSubscriptionWithAffiliates={hasPlanSubscriptionWithAffiliates}
            countryCode={countryCode}
          />
        </Col>
        <Col md="9" lg="9">
          <div className="mobileMenu d-lg-none d-md-none d-sm-block d-xs-block">
            <div className="navbar navbar-light pr-0">
              <button
                className="navbar-toggler"
                type="button"
                onClick={() => setMobileCollapse(!mobileCollapse)}
                data-toggle="collapse"
                data-target="#mobileNav"
                aria-controls="mobileNav"
                aria-expanded="false"
                aria-label="Toggle navigation"
              >
                <img className="navbar-toggler-icon-custom" src={BurgerMenuIcon} alt="burge-icon" />
              </button>
            </div>
            <div className={`navbar-collapse collapse ${mobileCollapse && 'show'} mb-4`} id="mobileNav">
              <Navigation tab={tab} history={props.history} client={props.client} handleBackToMyAccount={handleBackToMyAccount} isSubscriptionFromApps={isSubscriptionFromApps} countryCode={countryCode} hasInvoices={hasInvoices && !hasPreMigrationSubscription} hasPreMigrationSubscription={hasPreMigrationSubscription} hasPlanSubscriptionWithFreepass={hasPlanSubscriptionWithFreepass} hasPlanSubscriptionWithAffiliates={hasPlanSubscriptionWithAffiliates} />
            </div>
          </div>
          <Suspense fallback={<div />}>
            {tab === 'general' &&
              <GeneralInformation
                user={user}
              />}
            {tab === 'myaccount' && <Subscription planSubscription={planSubscription} addOnSubscriptions={addOnsSubscription} {...props} hasPreMigrationSubscription={hasPreMigrationSubscription} />}
            {tab === 'payment-methods' && (<PaymentMethods {...props} showTitle={false} />)}
            {tab === 'new-payment-method' && (<NewPaymentMethod {...props} showTitle={false} />)}
            {tab === 'invoices' && <InvoicesHistory invoices={invoices} {...props} />}
          </Suspense>
        </Col>
      </Row>
    </div>
  );
};

MyAccount.propTypes = {
  cancelPlanSubscription: PropTypes.func.isRequired,
  getAllInvoicesBySubscriber: PropTypes.func.isRequired,
  path: PropTypes.string.isRequired,
  client: PropTypes.string.isRequired,
  subscriptionMethodId: PropTypes.string,
  isSubscriptionFromApps: PropTypes.bool.isRequired,
  planSubscription: PropTypes.object,
  addOnsSubscription: PropTypes.array,
  invoices: PropTypes.array,
  history: PropTypes.shape({
    goBack: PropTypes.func,
    push: PropTypes.func,
  }).isRequired,
  parameters: PropTypes.shape({
    return_url: PropTypes.string,
  }),
  user: PropTypes.object,
  cancellationFlow: PropTypes.object,
  getPaymentMethodsByUser: PropTypes.func,
  changeFavoritePaymentMethod: PropTypes.func,
  paymentConfig: PropTypes.object,
  config: PropTypes.object,
  intl: PropTypes.object,
  deviceCountry: PropTypes.string,
  deletePaymentMethodsToUser: PropTypes.func,
  currentPaymentMethod: PropTypes.string
};

export default MyAccount;
